import { getBackgroundColorClass, getColorClass } from "@/lib/colors";
import gitlabIcon from "@/assets/icons/integrations/gitlab.svg";
import type { TriggerProps } from "@/ui/trigger";
import type { TriggerEventContext, TriggerRenderer, TriggerRendererContext } from "../types";
import { buildGitlabSubtitle } from "./utils";
import type { GitLabNodeMetadata } from "./types";

interface VulnerabilityObjectAttributes {
  title?: string;
  severity?: string;
  state?: string;
  report_type?: string;
  url?: string;
  project_id?: number;
}

interface OnVulnerabilityEventData {
  object_kind?: string;
  object_attributes?: VulnerabilityObjectAttributes;
}

function getVulnerabilityTitle(attributes: VulnerabilityObjectAttributes | undefined): string {
  return attributes?.title || "Vulnerability";
}

function getVulnerabilitySubtitle(attributes: VulnerabilityObjectAttributes | undefined): string {
  return attributes?.severity || attributes?.state || "";
}

function attributeOrEmpty(value: string | number | undefined): string {
  if (value == null) {
    return "";
  }
  return String(value);
}

export const onVulnerabilityTriggerRenderer: TriggerRenderer = {
  getTitleAndSubtitle: (context: TriggerEventContext) => {
    const eventData = context.event?.data as OnVulnerabilityEventData;
    const attrs = eventData?.object_attributes;

    return {
      title: getVulnerabilityTitle(attrs),
      subtitle: buildGitlabSubtitle(getVulnerabilitySubtitle(attrs), context.event?.createdAt),
    };
  },

  getRootEventValues: (context: TriggerEventContext): Record<string, string> => {
    const eventData = context.event?.data as OnVulnerabilityEventData;
    const attrs = eventData?.object_attributes;

    return {
      URL: attributeOrEmpty(attrs?.url),
      Title: attributeOrEmpty(attrs?.title),
      Severity: attributeOrEmpty(attrs?.severity),
      State: attributeOrEmpty(attrs?.state),
      "Report Type": attributeOrEmpty(attrs?.report_type),
      "Project ID": attributeOrEmpty(attrs?.project_id),
    };
  },

  getTriggerProps: (context: TriggerRendererContext): TriggerProps => {
    const { node, definition, lastEvent } = context;
    const metadata = node.metadata as unknown as GitLabNodeMetadata;
    const metadataItems = [];

    if (metadata?.project?.name) {
      metadataItems.push({
        icon: "book",
        label: metadata.project.name,
      });
    }

    const props: TriggerProps = {
      title: node.name || definition.label || "Unnamed trigger",
      iconSrc: gitlabIcon,
      iconColor: getColorClass(definition.color),
      collapsedBackground: getBackgroundColorClass(definition.color),
      metadata: metadataItems,
    };

    if (lastEvent) {
      const eventData = lastEvent.data as OnVulnerabilityEventData;
      const attrs = eventData?.object_attributes;

      props.lastEventData = {
        title: getVulnerabilityTitle(attrs),
        subtitle: buildGitlabSubtitle(getVulnerabilitySubtitle(attrs), lastEvent.createdAt),
        receivedAt: new Date(lastEvent.createdAt!),
        state: "triggered",
        eventId: lastEvent.id!,
      };
    }

    return props;
  },
};
